<template>
  <!-- Table Container Card -->
  <b-card no-body>
    <div class="m-2">
      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('CREATE_HOST_FINANCIAL_INSTITUTION')"
            :label="$t('Add')"
            size="md"
            @click="showModal('modal-lg')"
          />
          <b-dropdown
            v-if="getUserPermission('EXPORT_ECHANNEL_REPORT')"
            id="dropdown-1"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            class="ml-1"
            :text="$t('Export')"
            variant="primary"
          >
            <b-dropdown-item @click="csvExport">
              {{ $t('CSV') }}
            </b-dropdown-item>
            <b-dropdown-item @click="excelExport">
              {{ $t('Excel') }}
            </b-dropdown-item>
          </b-dropdown>
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="hostFinancialInstitution"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :items="hostFinancialInstitutionList"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(dateCreated)="data">
        {{ toDateWithYear(data.value) }}
      </template>

      <template #cell(status)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">
        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('MODIFY_HOST_FINANCIAL_INSTITUTION')"
              @click="onSelectHostInstitutionItem(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50"> {{ $t('Modify') }} </span>
            </b-dropdown-item>

            <b-dropdown-item @click="onSelectHostInstitutionItem(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50"> {{ $t('View') }} </span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="
                getUserPermission(
                  data.item.status === 'Active'
                    ? 'DEACTIVATE_HOST_FINANCIAL_INSTITUTION'
                    : 'ACTIVATE_HOST_FINANCIAL_INSTITUTION'
                )
              "
              @click="hostFinancialInstitutionStatus(data.item, $event.target)"
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{
                data.item.status === 'Active' ? $t('Deactivate') : $t('Activate')
              }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>
    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      :ok-title="buttonLabel"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      :ok-disabled="isLoading"
      centered
      no-close-on-backdrop
      size="lg"
      :title="modalTitle"
      @ok="handleSubmit"
      @hidden="resetModal"
    >
      <validation-observer ref="hostFinancialInstitutionRules">
        <b-form>
          <b-row class="match-height">
            <!--  host-FI-name -->
            <b-col md="6">
              <InputField
                id="host-FI-name"
                v-model="hostFinancialInstitutionFormData.name"
                :label="$t('Host FI Name')"
                type="text"
                :error-name="$t('Host FI Code')"
                rules="required"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- host-FI-name -->

            <!-- Host FI Code -->
            <b-col md="6">
              <InputField
                id="host-FI-code"
                v-model="hostFinancialInstitutionFormData.code"
                :label="$t('Host FI Code')"
                :placeholder="$t('Enter Host FI Code')"
                type="text"
                :error-name="$t('Host FI Code')"
                rules="required"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Host FI Code -->

            <!-- FTP Location -->
            <b-col md="6">
              <InputField
                id="ftp-location"
                v-model="hostFinancialInstitutionFormData.ftpLocation"
                :label="$t('FTP Location')"
                :placeholder="$t('Enter FTP Location')"
                type="text"
                :error-name="$t('FTP Location')"
                rules="required"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- FTP Location -->

            <!--Username -->
            <b-col md="6">
              <InputField
                id="username"
                v-model="hostFinancialInstitutionFormData.ftpUserName"
                :label="$t('Username')"
                :placeholder="$t('Enter Username')"
                type="text"
                :error-name="$t('Username')"
                rules="required"
                :readonly="isReadonly"
              />
            </b-col>
            <!--Username -->

            <!--Password -->
            <b-col md="6">
              <InputPasswordField
                id="password"
                v-model="hostFinancialInstitutionFormData.ftpPassword"
                :label="$t('Password')"
                :placeholder="$t('Enter Password')"
                type="text"
                :error-name="$t('Password')"
                rules="required"
                :readonly="isReadonly"
                :show-password-icon="false"
              />
            </b-col>
            <!-- Password -->
          </b-row>
        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{}"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      no-close-on-backdrop
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchHostFinancialInstitution"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!--  Host FI Name -->
        <b-col md="6">
          <InputField
            id="host-FI-name"
            v-model="filter.name"
            :label="$t('Host FI Name')"
            :placeholder="$t('Enter Host FI Name')"
            type="text"
          />
        </b-col>
        <!--  Host FI Name -->

        <!-- Host FI Code -->
        <b-col md="6">
          <InputField
            id="staff-id"
            v-model="filter.code"
            :label="$t('Host FI Code')"
            :placeholder="$t('Enter Host FI Code')"
            type="text"
          />
        </b-col>
        <!-- Host FI Code -->

        <!-- Status -->
        <b-col md="6">
          <SelectField
            id="status"
            v-model="filter.status"
            :label="$t('Status')"
            :placeholder="$t('Select')"
            :options="statusOptions"
            item="label"
            :reduce="option => option"
          />
        </b-col>
        <!-- Status -->
      </b-row>
    </b-modal>
    <!-- Filter modal -->
    <FullScreenSpinner :show="showSpinner" />
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BBadge, BModal, BForm, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { mapGetters, mapActions } from 'vuex'
import { required, minPhoneVal, maxPhoneVal } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import Ripple from 'vue-ripple-directive'
import { heightFade } from '@core/directives/animations'
import { permissionMixin } from '@core/mixins/permission'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import InputField from '../../../forms/InputField.vue'
import SelectField from '../../../forms/SelectField.vue'
import { gender, accessLevel } from '../../../../helpers/data'
import getStatusColor from '../../../../helpers/getStatusColor'
import Button from '../../../components/button/Button.vue'
// import { checkTokenValidity } from '@/helpers/token'
import { toDateWithYear } from '../../../../helpers/formatDate'
import { eChannelsPermission } from '../../../../helpers/permissions'
import InputPasswordField from '../../../forms/InputPasswordField.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BDropdown,
    BDropdownItem,
    BBadge,
    TablePagination,
    BModal,
    BForm,
    BSpinner,
    TableEntries,
    SelectField,
    ValidationObserver,
    InputField,
    InputPasswordField,
    Button,
    FullScreenSpinner,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toDateWithYear,
      isReadonly: false,
      isEditable: false,
      isFullName: false,
      showSpinner: false,
      sortBy: 'name',
      required,
      minPhoneVal,
      maxPhoneVal,
      modalTitle: this.$t('Host Financial Institution Setup'),
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'name', sortable: true, label: 'Host FI Name' },
        { key: 'code', sortable: true, label: 'Host FI Code' },
        { key: 'ftpLocation', sortable: true, label: 'FTP Location' },
        { key: 'dateCreated', sortable: true, label: 'Date Created' },
        { key: 'status', sortable: true, label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ],
      genderOptions: gender,
      getStatusColor,
      buttonLabel: this.$t('Save'),
      hostFinancialInstitutionFormData: {
        name: '',
        code: '',
        ftpLocation: '',
        ftpUserName: '',
        ftpPassword: '',
      },
      filter: {
        name: '',
        code: '',
        status: '',
        sortColumn: '',
      },
      accessLevelOptions: accessLevel,
      hostFinancialInstitutionList: [],
    }
  },
  computed: {
    ...mapGetters({
      branchOptions: 'operations/branchOptions',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchHostFinancialInstitution()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchHostFinancialInstitution()
    },
  },
  mounted() {
    this.fetchHostFinancialInstitution()
  },
  methods: {
    ...mapActions({
      getHostFIList: 'eChannels/getHostFIList',
      updateHostFinancialInstitution: 'eChannels/updateHostFIAction',
      createHostFinancialInstitution: 'eChannels/createHostFIAction',
      updateHostFinancialInstitutionStatus: 'eChannels/updateStatus',
      downloadHostFinacialInstitutionReport: 'reports/downloadHostFinacialInstitutionReport',
    }),
    async fetchHostFinancialInstitution() {
      try {
        this.isBusy = true
        this.hideModal('modal-filter')
        const response = await this.getHostFIList({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: eChannelsPermission.VIEW_HOST_FINANCIAL_INSTITUTION,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.hostFinancialInstitutionList = data.data
          this.totalCount = data.recordCount
        } else {
          this.hostFinancialInstitutionList = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    hostFinancialInstitutionStatus(items) {
      const { id, status } = items
      const action = status === 'Active' ? this.$t('Deactivate') : this.$t('Activate')
      const cardStatus = status === 'Active' ? 'InActive' : 'Active'
      const permission = status === 'Active'
        ? eChannelsPermission.DEACTIVATE_HOST_FINANCIAL_INSTITUTION
        : eChannelsPermission.ACTIVATE_HOST_FINANCIAL_INSTITUTION

      const updateStatus = async () => {
        try {
          this.showSpinner = true
          const statusValues = {
            id,
            status: cardStatus,
          }
          const response = await this.updateHostFinancialInstitutionStatus({
            statusValues,
            routeName: 'HostFI',
            permission,
          })
          if (response.status === 200) {
            this.fetchHostFinancialInstitution()
            const text1 = this.$t('Your request to')
            const text2 = this.$t('Host FI status was successful')
            this.alertSuccess({ text: `${text1} ${action} ${text2}` })
            this.showSpinner = false
          }
        } catch (error) {
          const text1 = this.$t('Your request to')
          const text = this.$t('Host FI  status failed. Reason')
          this.alertError({ text: `${text1} ${action} ${text}: ${error}` })
        } finally {
          this.showSpinner = false
        }
      }
      const areYouSure = this.$t('Are you sure you want to')
      const text = this.$t('this Host FI?')
      this.confirm(updateStatus, { title: '', text: `${areYouSure} ${action} ${text}` })
    },
    handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.hostFinancialInstitutionRules.validate().then(success => {
        if (success) {
          const areYouSure = this.$t('Are you sure you want to')
          const text = this.$t('this Host FI?')
          this.confirm(this.hostFinancialInstitutionAction, { text: `${areYouSure} ${this.buttonLabel} ${text}` })
        }
      })
    },
    async hostFinancialInstitutionAction() {
      try {
        this.isLoading = true
        const response = this.buttonLabel === this.$t('Save')
          ? await this.createHostFinancialInstitution(this.hostFinancialInstitutionFormData)
          : await this.updateHostFinancialInstitution(this.hostFinancialInstitutionFormData)
        const { status } = response
        if (status === 200) {
          this.fetchHostFinancialInstitution()
          this.hideModal('modal-lg')
          const text1 = this.$t('Your request to')
          const text2 = this.$t('Host Financial Institution was successful')
          this.alertSuccess({ text: `${text1} ${this.buttonLabel} ${text2}` })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
      }
    },
    async onSelectHostInstitutionItem(items, activity, val) {
      this.hostFinancialInstitutionFormData = {
        id: items.id,
        name: items.name,
        code: items.code,
        ftpLocation: items.ftpLocation,
        ftpUserName: items.ftpUserName,
        ftpPassword: items.ftpPassword,
      }
      this.isReadonly = val === 'edit' ? (this.isReadonly = false) : (this.isReadonly = true)
      this.modalTitle = val === 'edit' ? this.$t('Edit Host Financial Institution Setup') : this.$t('Host Financial Institution')
      this.buttonLabel = this.$t('Update')
      this.$root.$emit('bv::show::modal', 'modal-lg')
    },
    async csvExport() {
      try {
        this.isBusy = true
        const response = await this.downloadHostFinacialInstitutionReport({
          search: this.filter,
          fileExportFormat: 1,
          permission: eChannelsPermission.VIEW_HOST_FINANCIAL_INSTITUTION,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: this.$t('Your download is in progress and might take a little longer. Go to your'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('to access the completed file.'),
          })
        } else if (response.status === 504) {
          this.alertSuccessCustom({
            text: this.$t('Your download request is still processing, keep checking the'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('for status.'),
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async excelExport() {
      try {
        this.isBusy = true
        const response = await this.downloadHostFinacialInstitutionReport({
          search: this.filter,
          fileExportFormat: 0,
          permission: eChannelsPermission.VIEW_HOST_FINANCIAL_INSTITUTION,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: this.$t('Your download is in progress and might take a little longer. Go to your'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('to access the completed file.'),
          })
        } else if (response.status === 504) {
          this.alertSuccessCustom({
            text: this.$t('Your download request is still processing, keep checking the'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('for status.'),
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    resetModal() {
      this.hostFinancialInstitutionFormData = { ...null }
      this.isLoading = false
      this.isEditable = false
      this.isReadonly = false
      this.buttonLabel = this.$t('Save')
      this.modalTitle = this.$t('Host Financial Institution Setup')
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchHostFinancialInstitution()
    },
  },
}
</script>
